import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../../components/Footer';
import Header from '../../../components/Header';
import ScrollToTopButton from '../../../components/ScrollToTopButton';
import './DeveloperAccountGuide.scss';

const DeveloperAccountGuide: React.FC = () => {
  useEffect(() => {
    const nodes = document.querySelectorAll('.dev-account-guide-page .reveal');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    nodes.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="dev-account-guide-page">
      <Header />

      <section className="page-header">
        <div className="container reveal">
          <div className="breadcrumb">
            <Link to="/">Home</Link> <span>· 서비스신청 · 개발자계정등록안내</span>
          </div>
          <div className="page-header-grid">
            <div>
              <h1 className="page-title">
                우리 교회 이름으로 앱을,
                <br />
                <span className="em">개발자 계정</span> 등록 안내
              </h1>
              <p className="page-sub">
                앱스토어(App Store)와 플레이스토어(Google Play)에 교회 앱을 등록하려면
                교회 명의의 개발자 계정이 필요합니다. 신청 전 아래 안내를 참고해 미리
                준비해 주시면 제작 및 심사 일정이 단축됩니다.
              </p>
            </div>
            <div className="page-meta">Developer Account</div>
          </div>
        </div>
      </section>

      <section className="dag-section">
        <div className="container">
          <div className="dag-intro reveal">
            <p className="dag-intro__desc">
              개발자 계정은 앱을 배포하는 &apos;명의&apos;입니다. 저희가 앱 제작 · 등록 ·
              업데이트를 전부 대행하더라도, 완성된 앱은 교회가 소유한 개발자 계정으로
              게시되어야 이후 소유권과 운영 권한이 온전히 교회에 남습니다. 계정 등록 자체는
              교회 담당자가 직접 진행해 주셔야 하며, 등록 이후 저희 팀을 &apos;사용자(팀원)&apos;로
              초대해 주시면 나머지 작업은 저희가 진행합니다.
            </p>
          </div>
        </div>
      </section>

      <section className="dag-section">
        <div className="container">
          <div className="dag-platform-grid reveal">
            <div className="dag-panel">
              <span className="dag-panel__tag">iOS · App Store</span>
              <h2 className="dag-panel__title">Apple Developer Program</h2>
              <p className="dag-panel__desc">
                아이폰용 앱을 App Store에 올리기 위한 Apple의 공식 개발자 프로그램입니다.
                교회(단체) 명의로 가입하는 것을 권장합니다.
              </p>

              <div className="dag-summary-card">
                <div className="dag-summary-card__row">
                  <span className="dag-summary-card__label">연회비</span>
                  <span className="dag-summary-card__value">연 $99 (약 13~14만원)</span>
                </div>
                <div className="dag-summary-card__row">
                  <span className="dag-summary-card__label">준비물</span>
                  <span className="dag-summary-card__value">
                    Apple ID, 사업자등록증(단체 가입 시), D-U-N-S 번호
                  </span>
                </div>
                <div className="dag-summary-card__row">
                  <span className="dag-summary-card__label">소요 기간</span>
                  <span className="dag-summary-card__value">보통 2~5영업일 (D-U-N-S 발급 포함 시 최대 2주)</span>
                </div>
              </div>

              <ol className="dag-steps">
                <li>
                  <strong>Apple ID 준비</strong>
                  <span>기존 개인 Apple ID를 써도 되지만, 교회 전용 이메일로 새로
                    만들어 두면 이후 담당자 변경 시 관리가 편합니다.</span>
                </li>
                <li>
                  <strong>단체(Organization)로 가입</strong>
                  <span>개인이 아닌 &apos;단체&apos; 유형으로 가입해야 교회 명의로 앱이
                    게시됩니다. 단체 가입 시 D-U-N-S 번호(무료, 발급까지 며칠 소요될 수
                    있음)가 필요하며, Apple이 자동으로 조회해 발급을 도와주기도 합니다.</span>
                </li>
                <li>
                  <strong>가입 신청서 제출 및 연회비 결제</strong>
                  <span>단체명, 사업자등록번호 등 정보를 입력하고 연회비($99)를
                    결제하면 Apple의 심사(1~2일)를 거쳐 승인됩니다.</span>
                </li>
                <li>
                  <strong>저희 팀을 App Store Connect에 초대</strong>
                  <span>가입 완료 후 App Store Connect → 사용자 및 접근 권한 메뉴에서
                    저희가 안내드리는 이메일을 &apos;개발자(Developer)&apos; 권한으로
                    초대해 주세요.</span>
                </li>
              </ol>

              <a
                className="dag-official-link"
                href="https://developer.apple.com/programs/enroll/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Apple Developer Program 가입 페이지 →
              </a>
            </div>

            <div className="dag-panel">
              <span className="dag-panel__tag">Android · Google Play</span>
              <h2 className="dag-panel__title">Google Play Console</h2>
              <p className="dag-panel__desc">
                안드로이드 앱을 Google Play에 올리기 위한 Google의 공식 개발자 콘솔입니다.
                1회 등록비만 내면 계정을 계속 유지할 수 있습니다.
              </p>

              <div className="dag-summary-card">
                <div className="dag-summary-card__row">
                  <span className="dag-summary-card__label">등록비</span>
                  <span className="dag-summary-card__value">최초 1회 $25 (약 3~4만원)</span>
                </div>
                <div className="dag-summary-card__row">
                  <span className="dag-summary-card__label">준비물</span>
                  <span className="dag-summary-card__value">Google 계정, 결제 가능한 카드, 신분증(본인 확인용)</span>
                </div>
                <div className="dag-summary-card__row">
                  <span className="dag-summary-card__label">소요 기간</span>
                  <span className="dag-summary-card__value">계정 생성 즉시 ~ 최대 2~3일(본인 확인 심사)</span>
                </div>
              </div>

              <ol className="dag-steps">
                <li>
                  <strong>Google 계정 준비</strong>
                  <span>교회 전용 Google 계정을 새로 만드는 것을 권장합니다. 이후
                    담당자가 바뀌어도 계정을 그대로 인계할 수 있습니다.</span>
                </li>
                <li>
                  <strong>Play Console 개발자 계정 생성</strong>
                  <span>단체(조직) 계정으로 가입을 선택하고 교회명, 주소, 연락처를
                    입력한 뒤 등록비 $25를 결제합니다.</span>
                </li>
                <li>
                  <strong>본인/단체 확인 절차</strong>
                  <span>Google이 요청하는 신분증 또는 사업자 정보로 계정을 확인합니다.
                    확인이 끝나야 앱을 실제로 게시할 수 있습니다.</span>
                </li>
                <li>
                  <strong>저희 팀을 Play Console에 초대</strong>
                  <span>Play Console → 사용자 및 권한 메뉴에서 저희가 안내드리는
                    이메일을 초대하고, &apos;앱 게시&apos;·&apos;앱 정보 수정&apos;
                    권한을 부여해 주세요.</span>
                </li>
              </ol>

              <a
                className="dag-official-link"
                href="https://play.google.com/console/about/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google Play Console 시작하기 →
              </a>
            </div>
          </div>

          <div className="dag-note reveal">
            <h3 className="dag-note__title">등록 전 꼭 확인해 주세요</h3>
            <ul>
              <li>
                두 계정 모두 <strong>개인 명의가 아닌 교회(단체) 명의</strong>로 가입해야
                이후 담당자가 바뀌어도 앱 소유권 분쟁 없이 인계가 가능합니다.
              </li>
              <li>
                가입 시 등록비(Apple $99/년, Google $25/최초 1회)는 <strong>교회에서
                직접 결제</strong>하는 비용으로, 저희 서비스 이용료와 별도입니다.
              </li>
              <li>
                계정 아이디/비밀번호를 저희에게 알려주실 필요는 없습니다. 위 안내대로
                &apos;사용자 초대&apos; 방식으로 권한만 부여해 주시면 충분합니다.
              </li>
              <li>
                이미 개발자 계정이 있으시다면 이 단계는 생략하고 바로 서비스 신청을
                진행하셔도 됩니다.
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="final-cta">
        <div className="container reveal">
          <h2>
            계정 등록이
            <br />
            <span className="em">어려우신가요</span>?
          </h2>
          <p>등록 과정에서 막히는 부분이 있으면 문의해 주세요. 화면 공유로도 도와드립니다.</p>
          <Link to="/inquiry" className="btn-cream">
            문의하기 →
          </Link>
        </div>
      </section>

      <Footer />
      <ScrollToTopButton />
    </div>
  );
};

export default DeveloperAccountGuide;
